import { useState, useEffect } from 'react';
import { AuthGuard } from '../components/AuthGuard';
import { supabase } from '../lib/supabase';

interface Subscriber {
  id: string;
  email: string;
  source: string;
  created_at: string;
}

interface SentNewsletter {
  id: string;
  subject: string;
  sent_at: string;
  open_rate: number;
  click_rate: number;
}

export function NewsletterDashboard() {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [newsletters, setNewsletters] = useState<SentNewsletter[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [subscribersData, newslettersData] = await Promise.all([
        supabase.from('subscribers').select('*').order('created_at', { ascending: false }),
        supabase.from('sent_newsletters').select('*').order('sent_at', { ascending: false })
      ]);

      if (subscribersData.error) throw subscribersData.error;
      if (newslettersData.error) throw newslettersData.error;

      setSubscribers(subscribersData.data);
      setNewsletters(newslettersData.data);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Loading Dashboard...</h2>
          <p className="text-gray-600">Please wait while we fetch the latest data.</p>
        </div>
      </div>
    );
  }

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-3xl font-bold mb-8">Newsletter Dashboard</h1>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold mb-2">Total Subscribers</h3>
                <p className="text-3xl font-bold text-blue-600">{subscribers.length}</p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold mb-2">Newsletters Sent</h3>
                <p className="text-3xl font-bold text-blue-600">{newsletters.length}</p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold mb-2">Average Open Rate</h3>
                <p className="text-3xl font-bold text-blue-600">
                  {newsletters.length > 0
                    ? `${(newsletters.reduce((acc, n) => acc + n.open_rate, 0) / newsletters.length).toFixed(1)}%`
                    : '0%'}
                </p>
              </div>
            </div>

            {/* Recent Subscribers */}
            <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
              <h2 className="text-xl font-bold mb-4">Recent Subscribers</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Email</th>
                      <th className="text-left py-3 px-4">Source</th>
                      <th className="text-left py-3 px-4">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.slice(0, 10).map((subscriber) => (
                      <tr key={subscriber.id} className="border-b">
                        <td className="py-3 px-4">{subscriber.email}</td>
                        <td className="py-3 px-4">{subscriber.source}</td>
                        <td className="py-3 px-4">
                          {new Date(subscriber.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Recent Newsletters */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold mb-4">Recent Newsletters</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Subject</th>
                      <th className="text-left py-3 px-4">Sent Date</th>
                      <th className="text-left py-3 px-4">Open Rate</th>
                      <th className="text-left py-3 px-4">Click Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {newsletters.map((newsletter) => (
                      <tr key={newsletter.id} className="border-b">
                        <td className="py-3 px-4">{newsletter.subject}</td>
                        <td className="py-3 px-4">
                          {new Date(newsletter.sent_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4">{newsletter.open_rate}%</td>
                        <td className="py-3 px-4">{newsletter.click_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AuthGuard>
  );
}